import {
  ISessionSettings,
  ISettingsItem,
  ITaskItem,
  IPenaltyItem,
  IEventItem,
  IPermissionItem,
  TaskBasketItem,
  PenaltyBasketItem,
  ItemAdditionOptions,
  IItemSupport,
  ITaskTimerInfo,
  IPenaltyTimerInfo,
  EventItem,
  PermissionItem,
  ICustomScheduleEvent,
  ITaskBasketInfo,
  ISchedulerTimerInfo,
  TaskItem,
  IScheduledItem,
} from "../ts/Definitions";
import {
  ItemTypes,
  CountType,
  WeekendHandling,
  PreselectType,
  EventDay,
  TaskType,
} from "../ts/enums";
import {
  isTimedItem,
  getCountMinutes,
  getRandomCountForItem,
  isOutfitItem,
  isClothesItem,
  getRandomItemWithCache,
  getRandomItem,
  getPilloryTopicDefaults,
  isAvailableItem,
} from "../utilities/utils";
import { convertToEventDayFromDate } from "../utilities/dateUtils";
import {
  STATE_KEY_HISTORY_TASK_OPTIONS,
  STATE_KEY_HISTORY_TASK_ITEMS_USED,
  STATE_KEY_HISTORY_CREDITS,
  STATE_KEY_HISTORY_EVENTTASKS,
} from "../controllers/StatefulSettings";
import { cloneDeep as cloneObject } from "lodash";
import { differenceInMinutes, addMinutes, compareAsc, addDays } from "date-fns";

/**
 * BasketService handles all basket/cart item creation logic
 * Responsibilities:
 * - Creating basket items from settings items (tasks, penalties, events, permissions)
 * - Item selection logic (options, items, categories)
 * - Timer tick calculations with weekend handling
 * - Trigger item handling (events/permissions that spawn tasks)
 * - Custom schedule support
 */

// Callback interface to avoid circular dependencies
export interface BasketServiceCallbacks {
  // Item retrieval
  getTask: (key: string) => ITaskItem | undefined;
  getPenalty: (key: string) => IPenaltyItem | undefined;
  getEvent: (key: string) => IEventItem | undefined;
  getPermission: (key: string) => IPermissionItem | undefined;
  getTasks: (predicate?: (item: ITaskItem) => boolean) => ITaskItem[];

  // Settings and configuration
  getGeneralSettings: () => ISessionSettings;
  getTaskSettings: () => { creditCountVariance: number };
  getTaskSchedule: () => ITaskBasketInfo;
  getScheduleInfo: () => ISchedulerTimerInfo[];

  // Validation and checks
  canAddTask: (item: ISettingsItem, date?: Date, testRun?: boolean) => { isOk: boolean };
  checkItemIsOwned: (item: IItemSupport) => boolean;
  itemAvailableForDay: (item: ISettingsItem, eventDay: EventDay, includeHidden: boolean, ignoreAvailability: boolean) => boolean;

  // State management
  getState: <T>(key: string, defaultVal: T) => T;
  isTodayPreviewDay: () => boolean;
  getPreviewChoicesForItem: (item: ISettingsItem) => (TaskBasketItem | PenaltyBasketItem)[] | undefined;
  shouldReplaceRoutineItemOnPreview: (task: ITaskItem | IPenaltyItem) => boolean;

  // Caching for random selection (required by getRandomItemWithCache)
  getPreviousItemSelectionState: () => Record<string, string[]>;
  updatePreviousItemSelections: (key: string, vals: string[], add: boolean) => void;

  // Pillory support
  createPilloryTopic: (options: any) => void;

  // Audit support
  auditUser: (message: any) => void;

  // Item option selection
  getItemOptionSelectedToday: (item: ITaskItem | IPenaltyItem) => string | undefined;
}

export class BasketService {
  private callbacks: BasketServiceCallbacks;

  constructor(callbacks: BasketServiceCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Main entry point for creating basket items from a settings item
   */
  public createBasketItems(
    item: ISettingsItem,
    getIsWeekend: () => boolean,
    scheduleReason: string | undefined,
    date: Date,
    inputOptions?: ItemAdditionOptions,
    sourceItem?: ISettingsItem,
    testRun?: boolean
  ): (TaskBasketItem | PenaltyBasketItem)[] {
    if (!item) return [];

    switch (item.itemType) {
      case ItemTypes.event:
      case ItemTypes.permission: {
        let items: (TaskBasketItem | PenaltyBasketItem)[] = [];
        const triggerItems = this.handleTriggerItemSettings(
          sourceItem as EventItem | PermissionItem,
          getIsWeekend,
          date,
          inputOptions,
          testRun
        );
        const isCustomSchedule =
          this.callbacks.getEvent(item.key) === undefined &&
          item.itemType === ItemTypes.event;
        if (triggerItems?.length === 0 || isCustomSchedule) {
          // only add the acknowledgement if we have no other items
          items = this.createTaskBasketItem(
            item as ITaskItem,
            getIsWeekend,
            scheduleReason,
            date,
            sourceItem,
            inputOptions,
            testRun
          );
        }
        return items.concat(triggerItems);
      }
      case ItemTypes.task:
        return this.createTaskBasketItem(
          item as ITaskItem,
          getIsWeekend,
          scheduleReason,
          date,
          sourceItem,
          inputOptions,
          testRun
        );
      case ItemTypes.penalty:
        return this.createPenaltyBasketItems(
          item as IPenaltyItem,
          getIsWeekend,
          scheduleReason,
          date,
          inputOptions,
          testRun
        );
    }
    return [];
  }

  /**
   * Create a done/acknowledgement task for events
   */
  public getDoneTask(itemToAdd: ITaskItem): TaskBasketItem {
    const timerInfo: ITaskTimerInfo = {
      ...itemToAdd,
      isPaused: false,
      isComplete: true,
      completedTime: new Date(),
      isPenalty: false,
      creditTaskType: TaskType.acknowledgement,
      scheduleReason:
        "This is an acknowledgement of an event or permission that has fired to show the member that something has completed.",
    };
    return new TaskBasketItem(timerInfo);
  }

  /**
   * Get random timer ticks with weekend handling and variance
   */
  public getTaskTimerTicks(
    item: ITaskItem | IPenaltyItem,
    getIsWeekend: () => boolean
  ): number {
    const { count, countType } = item;
    if (!count) {
      return 1;
    }
    let countMinutes = getCountMinutes(count, countType);

    // Weekly numeric target tasks must keep their configured duration exactly
    // and are not subject to weekend or variance randomisation.
    if (item.itemType === ItemTypes.task && TaskItem.IsWeeklyNumericTargetTask(item as ITaskItem)) {
      return countMinutes;
    }

    const isWeekendDay = getIsWeekend();
    if (isWeekendDay) {
      const generalSettings = this.callbacks.getGeneralSettings();
      if (generalSettings.weekendHandling === WeekendHandling.harsh)
        countMinutes = countMinutes * 1.5;
      if (generalSettings.weekendHandling === WeekendHandling.easy)
        countMinutes = countMinutes / 2;
    }

    const creditSettings = this.callbacks.getTaskSettings();
    const randomisedCount = getRandomCountForItem(
      countMinutes,
      creditSettings.creditCountVariance,
      !isTimedItem(countType)
    );
    if (randomisedCount <= 1) return count; // makes no sense to return 0 times.
    return randomisedCount;
  }

  /**
   * Select items for a task that uses items
   * Handles item options, item groups, and nested item structures
   */
  public selectItemsForTask(
    item: ITaskItem | IPenaltyItem,
    itemAdditionOptions: ItemAdditionOptions | undefined,
    testRun?: boolean
  ): Record<string, string> | undefined {
    const itemOptionPickerFunc = (
      item: ITaskItem,
      optionSelections: Record<string, string>
    ): string => {
      if (!item.isItem) return "";
      if (!item.options) return item.name;

      // if we have injected a prior choice of what option this task should use, return that.
      if (optionSelections && optionSelections[item.key])
        return `${optionSelections[item.key]} ${item.name}`;

      let option = this.callbacks.getItemOptionSelectedToday(item);
      if (option) return option;
      if (!option) option = getRandomItem(item.options || []);
      if (!option) return item.name;
      return `${option} ${item.name}`;
    };

    // if i have already selected the options then just return what has been specifically set
    // and pick randomly for items in the selected item group not specifically set
    if (
      itemAdditionOptions &&
      itemAdditionOptions.selectedItemGroupKeys &&
      itemAdditionOptions.selectedItemGroupKeys.length > 0
    ) {
      const selectedOptions: Record<string, string> = {};
      itemAdditionOptions.selectedItemGroupKeys.forEach((key, idx) => {
        const optionItem = this.callbacks.getTask(key);
        if (!optionItem) return;
        if (!testRun && this.callbacks.canAddTask(optionItem as ISettingsItem, undefined, true).isOk === false) {
          return;
        }
        if (itemAdditionOptions.selectedItemGroupOptions
          && itemAdditionOptions.selectedItemGroupOptions[key]) {
          const option = itemAdditionOptions.selectedItemGroupOptions[key];
          selectedOptions[key] = `${option} ${optionItem?.name}`;
          return;
        }
        selectedOptions[key] = itemOptionPickerFunc(optionItem, {});
      });
      return selectedOptions;
    }

    // lets now construct the selection for each item in the group
    const selectedStrings: Record<string, string> = {};
    // here we want to look at each item we are going to choose and if we have already made a choice on that item, we will stick with that choice for today
    if (item.isItem) {
      if (!this.callbacks.checkItemIsOwned(item)) return undefined;
      if (!item.options) return undefined;
      // If a condition was explicitly set (e.g., from a custom schedule event), use it directly
      // rather than looking up today's active session option — which would be wrong for preview/tomorrow.
      if (itemAdditionOptions?.selectedCondition) {
        selectedStrings[item.key] = `${itemAdditionOptions.selectedCondition} ${item.name}`;
        return selectedStrings;
      }
      const previousOption = this.callbacks.getItemOptionSelectedToday(item);
      if (!previousOption) return undefined;
      selectedStrings[item.key] = previousOption;
      return selectedStrings;
    }

    // if it is an item already then return nothing, this is just returning sub item selections
    if (!item.itemsUsed || item.itemsUsed?.length === 0) return undefined;

    const ownedItemSelector = (arr: string[][]): string[][] => {
      // so we have sub items...  are the items themselves items or containers of further items.
      const availableItemCombinations: string[][] = [];
      arr
        .filter((items) => items.length > 0)
        .forEach((itemsCollection) => {
          if (
            itemsCollection.filter((i) =>
              this.callbacks.checkItemIsOwned(this.callbacks.getTask(i) as IItemSupport)
            ).length === itemsCollection.length
          ) {
            availableItemCombinations.push(itemsCollection);
          }
        });
      return availableItemCombinations;
    };
    const availableItemCombinations = ownedItemSelector(item.itemsUsed);

    // now we have those items which are either items they own, or groups of items where they own them all
    const selectedCombination = getRandomItemWithCache(
      availableItemCombinations || [],
      `${STATE_KEY_HISTORY_TASK_ITEMS_USED}${item.key}`,
      (items: string[]) => items?.join(),
      this.callbacks,
      !testRun
    );
    // now we have the one to describe. this could be items or groups of items so for each we need to derive
    if (!selectedCombination) return undefined;
    const selectedCredits = selectedCombination
      .map((c: string) => this.callbacks.getTask(c))
      .filter((p: ITaskItem | undefined) => p);
    // now let's go through each and pick an option for each item or if a group do it for each

    // fetch the mappings for the options for this selected combination which can preseed the results
    let itemPreselectionOptions = {};
    const taskItem = item as ITaskItem;
    if (taskItem.itemOptions && item.itemsUsed && selectedCombination)
      itemPreselectionOptions =
        taskItem.itemOptions[item.itemsUsed.indexOf(selectedCombination)];

    selectedCredits.forEach((credit: ITaskItem | undefined) => {
      if (!credit) return;
      // if the item is an item, then just add that in
      if (credit.isItem)
        selectedStrings[credit.key] = itemOptionPickerFunc(
          credit,
          itemPreselectionOptions
        );
      else if (!credit.itemsUsed) return;
      else {
        // we are a collection on further items under the item
        const subItemStrings: string[] = [];
        // work out which of the sub items we own
        const availableSubCombos = ownedItemSelector(credit.itemsUsed);
        // now we pick one from those available
        const selectedSubCombo = getRandomItem(availableSubCombos || []);
        if (!selectedSubCombo) return;
        // this can be a set of items itself each with their own options, so now add each in turn
        selectedSubCombo.forEach((subItem: string) => {
          const subCredit = this.callbacks.getTask(subItem);
          if (!subCredit) return;
          subItemStrings.push(
            itemOptionPickerFunc(subCredit, itemPreselectionOptions)
          );
        });
        selectedStrings[credit.key] = subItemStrings?.join();
      }
    });
    return selectedStrings;
  }

  /**
   * Create task basket item with all logic for options, items, timers, etc.
   */
  private createTaskBasketItem(
    itemToAdd: ITaskItem,
    getIsWeekend: () => boolean,
    scheduleReason: string | undefined,
    _date: Date,
    sourceItem?: ISettingsItem,
    inputOptions?: ItemAdditionOptions,
    testRun?: boolean
  ): TaskBasketItem[] {
    const retBasketItems = [];
    const itemOptions: ItemAdditionOptions = inputOptions
      ? { ...inputOptions }
      : {};
    const isCustomScheduled =
      sourceItem &&
      sourceItem.itemType === ItemTypes.event &&
      this.callbacks.getEvent(sourceItem.key) === undefined;

    if (TaskItem.IsTaskPickerTask(itemToAdd) && itemToAdd.selectCategoryItem) {
      const selectedItem = this.getTaskForCategory(
        itemToAdd.selectCategoryItem,
        itemToAdd,
        undefined,
        testRun
      );
      if (selectedItem) {
        retBasketItems.push(this.getDoneTask(itemToAdd));
        const taskReason = `Selected by ${itemToAdd.name}`;
        itemToAdd = selectedItem;
        if (!itemOptions?.reason) itemOptions.reason = taskReason;
      }
    }

    let expectedCount = itemOptions && itemOptions?.expectedCount;
    let expectedCountType =
      (itemOptions && itemOptions?.countType) || itemToAdd.countType;

    let selectedCondition = itemOptions?.selectedCondition;
    if (
      !selectedCondition &&
      !itemOptions.isPeek &&
      itemToAdd.options &&
      (!isOutfitItem(itemToAdd) || !itemOptions.selectedChoiceName) &&
      (!itemToAdd.useRouletteForOptions || itemToAdd.options.length === 1)
    )
      selectedCondition = getRandomItemWithCache(
        itemToAdd.options,
        `${STATE_KEY_HISTORY_TASK_OPTIONS}${itemToAdd.key}`,
        (s: string) => s,
        this.callbacks,
        !testRun
      );
    else if (isOutfitItem(itemToAdd) && itemOptions.selectedChoiceName) {
      selectedCondition = itemOptions.selectedChoiceName;
    } else if (!selectedCondition) selectedCondition = undefined;

    let eventMatch: IEventItem | undefined;
    if (!isCustomScheduled) {
      if (itemOptions && itemOptions.eventEndTrigger)
        itemToAdd.endTriggerEvent = itemOptions.eventEndTrigger;
      eventMatch = this.callbacks.getEvent(itemToAdd.endTriggerEvent as string);
      if (eventMatch && !isAvailableItem(eventMatch)) eventMatch = undefined;
    }

    if (!expectedCount && !eventMatch && itemToAdd.count) {
      const randomCount = this.getTaskTimerTicks(itemToAdd, getIsWeekend);
      expectedCount = randomCount;
      if (isTimedItem(expectedCountType)) expectedCountType = CountType.minutes;
    } else if (!expectedCount && itemToAdd.count) {
      // for all non timed items with a count and those timed items with count
      const countMinutes = getCountMinutes(itemToAdd.count, expectedCountType);
      if (!isNaN(countMinutes)) {
        expectedCount = countMinutes;
        if (isTimedItem(expectedCountType))
          expectedCountType = CountType.minutes;
      }
    }

    const timerInfo: ITaskTimerInfo = {
      ...itemToAdd,
      isPaused: true,
      ownerAdded: itemOptions?.ownerAdded,
      ownerName: itemOptions?.ownerName,
      requestedByKey: itemOptions?.requestedByKey,
      requestedByName: itemOptions?.requestedByName,
      customScheduleKey: itemOptions?.customScheduleKey,
      autoStartOnRequestedByStart: itemOptions?.autoStartOnRequestedByStart,
      selectedCondition,
      timerTicks: expectedCount,
      expectedCount,
      isComplete: false,
      reason: itemOptions?.reason,
      countType: expectedCountType,
      isPenalty: false,
      selectedChoiceName: itemOptions?.selectedChoiceName,
      credit: itemToAdd.credit,
      endTriggerEvent: eventMatch ? eventMatch.key : undefined,
      endTriggerEventName: eventMatch ? eventMatch.name : undefined,
      isRoulette: inputOptions?.isRoulette,
      isPillory: inputOptions?.isPillory,
      lovenseProfile: inputOptions?.lovenseProfile ?? itemToAdd.lovenseProfile,
      lovenseToyId: inputOptions?.lovenseToyId ?? itemToAdd.lovenseToyId,
      voteCount: inputOptions?.voteCount ?? itemToAdd.voteCount,
      scheduleReason: scheduleReason ?? '',
      timesRequired: itemToAdd.timesRequired,
    };

    if (!inputOptions?.isPeek) {
      // removed the exclude if custom schedule
      /// override the condition to be the persisted already chosen option for today if one has been set
      const items = this.selectItemsForTask(
        itemToAdd,
        itemOptions,
        testRun
      );
      if (itemToAdd.isItem) {
        if (items) timerInfo.selectedCondition = items[itemToAdd.key];
      } else timerInfo.selectedItems = items;
    }
    retBasketItems.push(new TaskBasketItem(timerInfo));
    return retBasketItems;
  }

  /**
   * Create penalty basket items
   */
  private createPenaltyBasketItems(
    itemToAdd: IPenaltyItem,
    getIsWeekend: () => boolean,
    scheduleReason: string | undefined,
    _date: Date,
    inputOptions?: ItemAdditionOptions,
    testRun?: boolean
  ): PenaltyBasketItem[] {
    if (!itemToAdd) return [];
    const retBasketItems: PenaltyBasketItem[] = [];
    const itemOptions = cloneObject(inputOptions);

    let expectedCount = itemOptions && itemOptions?.expectedCount;
    let expectedCountType =
      (itemOptions && itemOptions?.countType) || itemToAdd.countType;

    let selectedCondition = itemOptions?.selectedCondition;
    if (
      !selectedCondition &&
      itemToAdd &&
      itemToAdd.options &&
      (!itemToAdd.useRouletteForOptions || itemToAdd.options.length === 1)
    )
      selectedCondition = getRandomItemWithCache(
        itemToAdd.options,
        `${STATE_KEY_HISTORY_TASK_OPTIONS}${itemToAdd.key}`,
        (s: string) => s,
        this.callbacks,
        !testRun
      );

    if (itemOptions && itemOptions.eventEndTrigger)
      itemToAdd.endTriggerEvent = itemOptions.eventEndTrigger;

    let eventMatch = this.callbacks.getEvent(itemToAdd.endTriggerEvent as string);
    if (eventMatch && !isAvailableItem(eventMatch)) eventMatch = undefined;

    if (!expectedCount && !eventMatch && itemToAdd.count) {
      const randomCount = this.getTaskTimerTicks(itemToAdd, getIsWeekend);
      expectedCount = randomCount;
      if (isTimedItem(expectedCountType)) expectedCountType = CountType.minutes;
    } else if (!expectedCount && itemToAdd.count) {
      // for all non timed items with a count and those timed items with count
      const countMinutes = getCountMinutes(itemToAdd.count, expectedCountType);
      if (!isNaN(countMinutes)) {
        expectedCount = countMinutes;
        if (isTimedItem(expectedCountType))
          expectedCountType = CountType.minutes;
      }
    }

    const timerInfo: IPenaltyTimerInfo = {
      ...itemToAdd,
      isPaused: true,
      ownerAdded: itemOptions?.ownerAdded,
      ownerName: itemOptions?.ownerName,
      requestedByKey: itemOptions?.requestedByKey,
      requestedByName: itemOptions?.requestedByName,
      customScheduleKey: itemOptions?.customScheduleKey,
      autoStartOnRequestedByStart: itemOptions?.autoStartOnRequestedByStart,
      selectedCondition,
      timerTicks: expectedCount,
      expectedCount,
      isComplete: false,
      reason: itemOptions?.reason,
      countType: expectedCountType,
      isPenalty: true,
      endTriggerEvent: eventMatch ? eventMatch.key : undefined,
      endTriggerEventName: eventMatch ? eventMatch.name : undefined,
      isRoulette: inputOptions?.isRoulette,
      isPillory: inputOptions?.isPillory,
      lovenseProfile: inputOptions?.lovenseProfile ?? itemToAdd.lovenseProfile,
      lovenseToyId: inputOptions?.lovenseToyId ?? itemToAdd.lovenseToyId,
      voteCount: inputOptions?.voteCount ?? itemToAdd.voteCount,
      scheduleReason: scheduleReason ?? '',
    };

    const items = this.selectItemsForTask(
      itemToAdd,
      itemOptions,
      testRun
    );
    if (itemToAdd.isItem) {
      if (items) timerInfo.selectedCondition = items[itemToAdd.key];
    } else timerInfo.selectedItems = items;

    retBasketItems.push(new PenaltyBasketItem(timerInfo));
    return retBasketItems;
  }

  /**
   * Get available category tasks that can be added
   */
  public getAvailableCategoryTasks(
    selectedCategory: string,
    triggerItem: ISettingsItem,
    date?: Date,
    testRun?: boolean
  ): ITaskItem[] {
    if (!selectedCategory) return [];
    const taskItems = this.callbacks.getTasks(
      (c) =>
        c.key !== triggerItem.key &&
        c.categories !== undefined &&
        !c.selectCategoryItem &&
        c.categories?.filter((cat) => cat === selectedCategory).length > 0
    );
    return taskItems.filter((i) => this.callbacks.canAddTask(i, date, testRun).isOk);
  }

  /**
   * Get a random task for a category
   */
  private getTaskForCategory(
    selectedCategory: string,
    triggerItem: ISettingsItem,
    date?: Date,
    testRun?: boolean
  ): ITaskItem | undefined {
    const matches = this.getAvailableCategoryTasks(
      selectedCategory,
      triggerItem,
      date,
      testRun
    );
    if (matches.length === 0) {
      return;
    }
    const selectedItem = getRandomItemWithCache(
      matches,
      STATE_KEY_HISTORY_CREDITS,
      (t: ITaskItem) => t.key,
      this.callbacks,
      !testRun
    );
    return selectedItem;
  }

  /**
   * Handle trigger items (events/permissions) that create tasks
   * This is a complex method that handles:
   * - Preview choices
   * - Task keys, task categories, penalties, pillories
   * - Custom schedules with timing logic
   * - Single vs multiple task selection
   */
  private handleTriggerItemSettings(
    triggerItem: PermissionItem | EventItem,
    getIsWeekend: () => boolean,
    date: Date,
    inputOptions?: ItemAdditionOptions,
    testRun?: boolean
  ): (TaskBasketItem | PenaltyBasketItem)[] {
    let items: (TaskBasketItem | PenaltyBasketItem)[] = [];
    // Only suppress trigger tasks when decision was explicitly denied.
    if (!triggerItem || inputOptions?.decision === false) return items;

    const isCustomSchedule =
      triggerItem.itemType === ItemTypes.event &&
      this.callbacks.getEvent(triggerItem.key) === undefined;

    const isValidToAddTask = (taskKey: string): boolean => {
      const task = this.callbacks.getTask(taskKey) || this.callbacks.getPenalty(taskKey);
      const taskScheduleItem = this.callbacks.getTaskSchedule()[taskKey];
      return (
        task &&
        ((isCustomSchedule
          && this.callbacks.checkItemIsOwned(task)
          && this.callbacks.itemAvailableForDay(task, convertToEventDayFromDate(date.getDay()), false, false)
          && (!taskScheduleItem || taskScheduleItem.isComplete)
        ) ||
          this.callbacks.canAddTask(task, date, testRun).isOk)
      ) || false;
    };

    if (this.callbacks.isTodayPreviewDay() && !testRun) {
      // we have pre-selections for this event/permission
      const previewItems = this.callbacks.getPreviewChoicesForItem(triggerItem);
      if (previewItems && previewItems?.length > 0) {
        // add them and return
        previewItems.forEach((i) => {
          if (this.callbacks.shouldReplaceRoutineItemOnPreview(i)) return;
          if (!isValidToAddTask(i.key)) {
            return;
          }
          if (i.itemType === ItemTypes.task) {
            items.push(new TaskBasketItem(i as ITaskTimerInfo));
          } else if (i.itemType === ItemTypes.penalty) {
            items.push(new PenaltyBasketItem(i as IPenaltyTimerInfo));
          }
        });
        return items;
      }
    }

    if (triggerItem.taskKeys) {
      const schedule = this.callbacks.getScheduleInfo();
      const orderedEvents = schedule
        .filter(
          (c) =>
            c.startTime && differenceInMinutes(c.startTime, new Date()) > 0
        )
        .sort((x, y) =>
          !x.startTime || !y.startTime
            ? 0
            : compareAsc(x.startTime, y.startTime)
        );
      const nextEvent = orderedEvents && orderedEvents?.length > 0 && orderedEvents[0];
      const lastEvent =
        orderedEvents && orderedEvents?.length > 0 && orderedEvents[orderedEvents.length - 1];

      const isOutfitEventItem = (e: ISchedulerTimerInfo, tasks: ITaskItem[]): ITaskItem[] => {
        const task = tasks.find(t => t.key === e.key);
        if (!task) return [];
        return task.itemsUsed?.flat().map(k => tasks.find(t => t.key === k)).filter(t => t && isClothesItem(t)) as ITaskItem[] || [];
      };

      const nextOutfit = schedule
        .filter(
          (e) =>
            !e.isComplete && isOutfitEventItem(e, this.callbacks.getTasks())?.length > 0
        )
        .sort((x, y) =>
          !x.startTime || !y.startTime
            ? 0
            : compareAsc(x.startTime, y.startTime)
        )[0];

      const setDurationOverride = (
        additionInfo: ItemAdditionOptions,
        minutes: number | undefined
      ): void => {
        if (!minutes || minutes <= 0) return;
        additionInfo.countType = CountType.minutes;
        additionInfo.expectedCount = Math.max(1, Math.floor(minutes));
      };

      const addEventTask = (taskKey: string) => {
        const task = this.callbacks.getTask(taskKey) || this.callbacks.getPenalty(taskKey);
        if (task && isValidToAddTask(taskKey)) {
          const customEvent = triggerItem as ICustomScheduleEvent;
          let additionInfo: ItemAdditionOptions = {
            reason: `Selected by ${triggerItem.name}`,
            eventEndTrigger: triggerItem.tasksEndTriggerEvent,
            requestedByKey: triggerItem.key,
            requestedByName: triggerItem.name,
          };

          if (isCustomSchedule) {
            additionInfo = { reason: `Part of ${customEvent.name}` };
            additionInfo.requestedByKey = triggerItem.key;
            additionInfo.requestedByName = triggerItem.name;
            additionInfo.customScheduleKey = triggerItem.key;
            additionInfo.autoStartOnRequestedByStart = customEvent.awaitTaskComplete === true;

            if (nextEvent && nextEvent.startTime && customEvent.runTasksToNextEvent) {
              const diffInMinutes = differenceInMinutes(
                nextEvent.startTime,
                new Date()
              );
              setDurationOverride(additionInfo, diffInMinutes);
            } else if (!customEvent.runTasksToNextEvent && (customEvent.taskDurationMinutes ?? 0) > 0) {
              setDurationOverride(additionInfo, customEvent.taskDurationMinutes);
            }

            // Custom schedule timing logic for clothes/timed items
            if (additionInfo.expectedCount === undefined && nextOutfit && nextOutfit.startTime && isClothesItem(task)) {
              const diffInMinutes = differenceInMinutes(
                nextOutfit.startTime,
                new Date()
              );
              setDurationOverride(additionInfo, diffInMinutes);
            } else if (
              additionInfo.expectedCount === undefined &&
              lastEvent &&
              lastEvent.startTime &&
              isClothesItem(task)
            ) {
              const diffInMinutes = differenceInMinutes(
                addMinutes(lastEvent.startTime, lastEvent.duration ?? 0),
                new Date()
              );
              setDurationOverride(additionInfo, diffInMinutes);
            } else if (
              additionInfo.expectedCount === undefined &&
              task.endTriggerEvent &&
              lastEvent &&
              lastEvent.startTime
            ) {
              const diffInMinutes = differenceInMinutes(
                lastEvent.startTime,
                new Date()
              );
              setDurationOverride(additionInfo, diffInMinutes);
            }
            delete task.endTriggerEvent;

            const scheduleEvent = triggerItem as ICustomScheduleEvent;
            if (
              scheduleEvent &&
              scheduleEvent.itemOptions &&
              scheduleEvent.itemOptions[task.key]
            ) {
              additionInfo.selectedCondition =
                scheduleEvent.itemOptions[task.key];
              const concreteTask = task as ITaskItem;
              if (concreteTask.itemOptions) {
                let choiceOptionIdx = -1;
                if (concreteTask.choiceNames)
                  choiceOptionIdx = Object.values(concreteTask.choiceNames).findIndex(
                    (n) => n === additionInfo.selectedCondition
                  );
                if (choiceOptionIdx === -1) {
                  if (
                    additionInfo.selectedCondition
                      .toLowerCase()
                      .startsWith("option ")
                  ) {
                    choiceOptionIdx =
                      parseInt(
                        additionInfo.selectedCondition
                          .toLowerCase()
                          .replace("option ", "")
                      ) - 1;
                  }
                }
                if (choiceOptionIdx > -1) {
                  const items = task.itemsUsed
                    ? task.itemsUsed[choiceOptionIdx]
                    : [];
                  let options: Record<string, string> = {};
                  if (items && items.length > 0) {
                    options = concreteTask.itemOptions[choiceOptionIdx] ?? {};
                    const newOptions: Record<string, string> = {};
                    Object.keys(options)
                      .filter((k) => items.indexOf(k) > -1)
                      .forEach((k) => (newOptions[k] = options[k]));
                    options = newOptions;
                  }
                  additionInfo.selectedItemGroupOptions = options;
                  additionInfo.selectedItemGroupKeys = items;
                }
                additionInfo.selectedChoiceName =
                  additionInfo.selectedCondition;
              }
            }
          }

          if (task.itemType === ItemTypes.task) {
            const newItems = this.createTaskBasketItem(
              task as ITaskItem,
              getIsWeekend,
              `This task was added as a result of ${triggerItem.name} running. It was set in the settings of ${triggerItem.name} and was deemed available today based on the members diary and category limits.`,
              date,
              triggerItem,
              additionInfo
            );
            items = items.concat(newItems);
          } else if (task.itemType === ItemTypes.penalty) {
            const newItems = this.createPenaltyBasketItems(
              task as IPenaltyItem,
              getIsWeekend,
              `This penalty was added as a result of ${triggerItem.name} running. It was set in the settings of ${triggerItem.name} and was deemed available today based on the members diary and category limits.`,
              date,
              additionInfo
            );
            items = items.concat(newItems);
          }
        }
      };

      if (!triggerItem.pickSingleTask && triggerItem.taskKeys) {
        for (let i = 0; i < triggerItem.taskKeys?.length; i += 1) {
          const taskKey = triggerItem.taskKeys[i];
          addEventTask(taskKey);
        }
      } else if (triggerItem.pickSingleTask && triggerItem.taskKeys) {
        const taskKey = triggerItem.selectedTaskKeys && triggerItem.selectedTaskKeys.length > 0
          ? triggerItem.selectedTaskKeys[0]
          : getRandomItemWithCache(
            triggerItem.taskKeys,
            `${STATE_KEY_HISTORY_EVENTTASKS}${triggerItem.key}`,
            (i: string) => i,
            this.callbacks,
            !testRun
          );
        if (taskKey) {
          addEventTask(taskKey);
        }
      }

      if (triggerItem.penalties) {
        const addPenaltyTask = (taskKey: string) => {
          const task = this.callbacks.getPenalty(taskKey);
          if (task && this.callbacks.canAddTask(task, date, testRun).isOk) {
            const newItems = this.createPenaltyBasketItems(
              task,
              getIsWeekend,
              `This penalty was added as a result of ${triggerItem.name} running. It was set in the settings of ${triggerItem.name} and was deemed available today based on the members diary and category limits.`,
              date,
              {
                ...inputOptions,
                reason: `Selected by ${triggerItem.name}`,
                eventEndTrigger: triggerItem.tasksEndTriggerEvent,
                requestedByKey: triggerItem.key,
                requestedByName: triggerItem.name,
                customScheduleKey: isCustomSchedule ? triggerItem.key : undefined,
                autoStartOnRequestedByStart: isCustomSchedule
                  ? (triggerItem as ICustomScheduleEvent).awaitTaskComplete === true
                  : undefined,
              }
            );
            items = items.concat(newItems);
          }
        };
        if (!triggerItem.pickSinglePenalty && triggerItem.penalties) {
          for (let i = 0; i < triggerItem.penalties?.length; i += 1) {
            const taskKey = triggerItem.penalties[i];
            addPenaltyTask(taskKey);
          }
        } else if (triggerItem.pickSinglePenalty && triggerItem.penalties) {
          const taskKey = getRandomItem(triggerItem.penalties);
          if (taskKey) {
            addPenaltyTask(taskKey);
          }
        }
      }

      if (triggerItem.pillories) {
        const addPilloryTask = (taskKey: string) => {
          const task = this.callbacks.getPenalty(taskKey);
          if (task && this.callbacks.canAddTask(task, undefined, true).isOk) {
            const defaults = getPilloryTopicDefaults(task);
            this.callbacks.createPilloryTopic({
              addCountLimits: false,
              countType: defaults.countType,
              customCondition: false,
              date: addDays(new Date(), 1),
              item: task,
              reason: `This was added as a result of ${triggerItem.name} which selects this pillory.`,
              expectedCount: defaults.count,
              isLovenseToy: task.isLovenseToy,
              lovenseProfile: task.lovenseProfile,
              lovenseToyId: task.lovenseToyId,
              votable: true,
              voteCount: defaults.count,
            });
          }
        };
        if (!triggerItem.pickSinglePillory && triggerItem.pillories) {
          for (let i = 0; i < triggerItem.pillories?.length; i += 1) {
            const taskKey = triggerItem.pillories[i];
            addPilloryTask(taskKey);
          }
        } else if (triggerItem.pickSinglePillory && triggerItem.pillories) {
          const taskKey = getRandomItem(triggerItem.pillories);
          if (taskKey) {
            addPilloryTask(taskKey);
          }
        }
      }

      if (triggerItem.taskCategories) {
        const addTaskForCategory = (category: string) => {
          const task = this.getTaskForCategory(category, triggerItem);
          if (task && this.callbacks.canAddTask(task, date, testRun).isOk) {
            const newItems = this.createTaskBasketItem(
              task,
              getIsWeekend,
              `This task was added as a result of ${triggerItem.name} running. It was in a category of ${category} and was deemed available today based on the members diary and category limits.`,
              date,
              triggerItem,
              {
                reason: `Selected by ${triggerItem.name} for ${category}`,
                eventEndTrigger: triggerItem.tasksEndTriggerEvent,
                requestedByKey: triggerItem.key,
                requestedByName: triggerItem.name,
                customScheduleKey: isCustomSchedule ? triggerItem.key : undefined,
                autoStartOnRequestedByStart: isCustomSchedule
                  ? (triggerItem as ICustomScheduleEvent).awaitTaskComplete === true
                  : undefined,
              }
            );
            items = items.concat(newItems);
          }
        };
        if (!triggerItem.pickSingleCategoryTask && triggerItem.taskCategories) {
          for (let i = 0; i < triggerItem.taskCategories.length; i += 1) {
            const category = triggerItem.taskCategories[i];
            addTaskForCategory(category);
          }
        } else if (triggerItem.pickSingleCategoryTask && triggerItem.taskCategories) {
          if (triggerItem.selectedTaskKeys && triggerItem.selectedTaskKeys.length > 0) {
            addEventTask(triggerItem.selectedTaskKeys[0]);
          } else {
            const category = getRandomItem(triggerItem.taskCategories);
            if (category) {
              addTaskForCategory(category);
            }
          }
        }
      }
    }
    return items;
  }
}
